
import 'reflect-metadata';
import {
  Vue, Component, Prop, Watch, Provide, Model, Emit,
} from 'vue-property-decorator';
import Util from '../../../assets/utils/Util';
import InlineGridField from './InlineGridField.vue';
import Http from '../../../assets/utils/HttpRequest';
import ConfirmationDialog from '../../popups/ConfirmationDialog.vue';

import GridToolbar from '../GridToolbar.vue';
import SnackbarUtils from '../../../assets/utils/SnackbarUtils';

@Component({
  name: 'InlineGrid',
  components: {
    InlineGridField,
    ConfirmationDialog,
    GridToolbar,
  },
})
export default class InlineGrid extends Vue {
  private static readonly CUSTOM_CLASS_PREFIX = '$custom_';

  // #region [ PROPS ]
  @Prop({ default: false })
  private readonly cancelBtnDisabled: boolean;

  @Prop({ default: 'Cancelar' })
  private readonly cancelMessage: string;

  @Prop({ required: true })
  private readonly columns: [];

  @Prop({ default: () => [] })
  private readonly customActions: any[];

  @Prop({ default: '' })
  private readonly dataSource: string;

  @Prop({ default: '' })
  private readonly descriptionField: string;

  @Prop({ default: () => {} })
  private readonly filters: any;

  @Prop({ default: undefined })
  private readonly height: number;

  @Prop({ default: 'Nenhum resultado.' })
  private readonly noDataText: string;

  @Prop({ default: false })
  private readonly preventSave: boolean;

  @Prop({ required: true })
  private readonly primaryKey: string;

  @Prop({ default: false })
  private readonly readonly: boolean;

  @Prop({ default: false })
  private readonly reloadAfterSave: boolean;

  @Prop({ default: false })
  private readonly reloadOnCreated: boolean;

  @Prop({ default: undefined })
  private readonly rowClass: string | ((row: any) => string);

  @Prop({ required: true })
  private readonly rows: [];

  @Prop({ default: 'Salvar' })
  private readonly saveMessage: string;

  @Prop({ default: false })
  private readonly saveBtnDisabled: boolean;

  @Prop({ required: false, default: false })
  private readonly showSelect: boolean;

  @Model('selectedUpdated', { type: Array })
  private readonly selected: any;

  @Prop()
  private readonly toolbar_showDefaultActions: boolean;

  @Prop()
  private readonly toolbar_notShowWichDefaultActions: [];

  @Prop()
  private readonly toolbar_defaultActionsOnClick: {};

  @Prop()
  private readonly toolbar_detailsActions: [];

  @Prop()
  private readonly toolbar_additionalActions: [];

  @Prop()
  private readonly toolbar_buttons: [];

  @Prop()
  private readonly toolbar_buttonsDisabled: boolean;

  @Prop()
  private readonly toolbar_additionalActionsPosition: string;

  @Prop({ default: 0 })
  private readonly triggerReload: number;

  @Prop({ default: 0 })
  private readonly triggerCancel: number;

  @Prop({ default: 0 })
  private readonly triggerSave: number;
  // #endregion

  // #region [ EVENTS ]
  @Emit('after-reload')
  private emitAfterReload(rows: any[]) {}

  @Emit('after-save')
  private emitAfterSave() {}

  @Emit('edit')
  private emitEdit(cell: {row: any, column: any}) {}

  @Emit('item-selected')
  private itemSelected(item) {
    return { ...item, selected: this.selected };
  }

  @Emit('save')
  private emitSave(rows: any[]) {}

  @Emit('selectedUpdated')
  private emitSelectedChange() {
    return this.internSelected;
  }
  // #endregion

  // #region [ DATA ]
  private saveSnack = false;

  private currentRow: {};

  private gridRows = [];

  private dataBackup = [];

  private cancelDialogIsVisible = false;

  private static defaultErrorMessage = 'Não foi possível carregar os dados. Tente novamente mais tarde.';

  private fieldTriggers = {
    onOpen: {},
  };

  private internSelected = [];
  // #endregion

  // #region [ WATCHERS ]
  @Watch('columns')
  private columnsOnChange(newValue: any, oldValue: any): void {
    this.$forceUpdate();
  }

  @Watch('internSelected')
  private internSelectedOnChange(newValue: any) {
    this.emitSelectedChange();
  }

  @Watch('rows')
  private rowsOnChange(newValue: any): void {
    this.gridRows = Util.deepCopy(newValue);

    this.setRowKeys(this.gridRows);

    this.dataBackup = Util.deepCopy(this.gridRows);
    this.applyRowClass();
  }

  @Watch('selected')
  private selectedOnChange(newValue: any) {
    this.internSelected = newValue;
  }

  @Watch('triggerCancel')
  private triggerCancelOnChange(newValue: any, oldValue: any): void {
    this.showCancelDialog();
  }

  @Watch('triggerReload')
  private triggerReloadOnChange(newValue: any, oldValue: any): void {
    this.reload();
  }

  @Watch('triggerSave')
  private triggerSaveOnChange(newValue: any, oldValue: any): void {
    this.save();
  }
  // #endregion

  // #region [ LIFECYCLE ]
  private created() {
    (window as any).grid = this;

    this.gridRows = Util.deepCopy(this.rows);
    this.createBackup();
  }

  private mounted() {
    this.setDefaultToolbarDefaultActionsOnClick();
    this.applyRowClass();
  }
  // #endregion

  // #region [ METHODS ]
  private setDefaultToolbarDefaultActionsOnClick() {
    if (this.toolbar_defaultActionsOnClick && (this.toolbar_defaultActionsOnClick as any).refresh) {
      return this.toolbar_defaultActionsOnClick;
    }

    const inlineGridDefaultActions = this.toolbar_defaultActionsOnClick === undefined ? {} : { ...this.toolbar_defaultActionsOnClick };

    (inlineGridDefaultActions as any).refresh = () => this.reload();

    return inlineGridDefaultActions;
  }

  private focusField(row: any, column: any): void {
    // Timeout para selecionar o campo somente após o popup ter sido aberto
    setTimeout(() => {
      const field = document.getElementById(
        `__grid_field_${row.$_row_key}-${column.value}`,
      ) as any;

      if (field) {
        field.focus();

        if (field.select) {
          field.select();
        }
      }
    }, 200);
  }

  private closeEditDialog(row: any, column: any): void {
    const dialog = document.getElementById(
      `__grid_dialog_${row.$_row_key}-${column.value}`,
    ) as any;

    dialog.__vue__.save();
  }

  private setCurrentRow(row: any): void {
    this.currentRow = row;
  }

  private rowAfterEdit(editedRow: any, column: any, newValue: string): void {
    if (column.type === 'select') {
      this.closeEditDialog(editedRow, column);

      if (column.valueField) {
        const option = column.options.find((item) => item.value === newValue);
        const itemText = option ? option.text : '';

        editedRow[column.valueField] = newValue;
        newValue = itemText;
      }
    }

    if (editedRow[column.value] === newValue) {
      return;
    }

    editedRow.hasChanged = true;
    editedRow[column.value] = newValue;

    this.emitEdit({row: editedRow, column});
    this.$forceUpdate();
  }

  private cancel(): void {
    this.gridRows.forEach((row: any) => {
      this.undoEdition(row, false);
    });

    this.$forceUpdate();
  }

  private async save(): Promise<void> {
    await Http.fakeLoading(1500);

    this.saveSnack = true;

    this.gridRows.forEach((row: any) => {
      row.hasChanged = false;
    });

    this.dataBackup = Util.deepCopy(this.gridRows);

    this.emitAfterSave();
  }

  private undoEdition(item: any, update: boolean = true): void {
    const originalRow = this.dataBackup.find(
      (x: any) => x.$_row_key === item.$_row_key,
    );

    Object.keys(item).forEach((key: string) => {
      item[key] = originalRow[key];
    });

    if (update) {
      this.$forceUpdate();
    }
  }

  private fieldOnTab(row: any, column: any, newValue: any): void {
    this.openNextField(row, column);
    this.rowAfterEdit(row, column, newValue);
  }

  private fieldOnShiftTab(row: any, column: any, newValue: any): void {
    // Abre o campo editável anterior
    this.openPreviousField(row, column);
    this.rowAfterEdit(row, column, newValue);
  }

  private fieldOnArrowUp(row: any, column: any, newValue: any): void {
    this.openFieldAbove(row, column);
    this.rowAfterEdit(row, column, newValue);
  }

  private fieldOnArrowDown(row: any, column: any, newValue: any): void {
    this.openFieldUnder(row, column);
    this.rowAfterEdit(row, column, newValue);
  }

  private openNextField(row: any, column: any) {
    const nextCol = this.getNextEditableColumn(column.value);

    this.openField(row, column, row, nextCol);
  }

  private openPreviousField(row: any, column: any) {
    const previousCol = this.getNextEditableColumn(column.value, true);

    this.openField(row, column, row, previousCol);
  }

  private openFieldAbove(row: any, column: any): void {
    const rowAbove = this.getRowUnder(row, true);
    if (rowAbove) {
      this.openField(row, column, rowAbove, column);
    }
  }

  private openFieldUnder(row: any, column: any): void {
    const rowUnder = this.getRowUnder(row);
    if (rowUnder) {
      this.openField(row, column, rowUnder, column);
    }
  }

  private openField(row: any, column: any, rowToOpen: any, columnToOpen: any): void {
    if (!columnToOpen) {
      return;
    }

    const currentFieldlDialog = document.getElementById(
      this.getEditDialogId(row, column),
    ) as any;

    currentFieldlDialog.__vue__.save();

    const nextFieldDialog = document.getElementById(
      this.getEditDialogId(rowToOpen, columnToOpen),
    );

    const dialogParent = nextFieldDialog.parentElement;
    const nextFieldDialogActivator = Util.htmlFind(
      dialogParent.children,
      (child: HTMLElement) => child.className.includes('dialog__activator'),
    );

    if (nextFieldDialogActivator) {
      nextFieldDialogActivator.click();
    }
  }

  private getNextEditableColumn(currentColName: string, previous: boolean = false): any {
    const columnsCopy = [...this.columns];

    if (previous) {
      columnsCopy.reverse();
    }

    const editableCols = columnsCopy.filter(
      (col: any) => col.readOnly !== true,
    );

    let afterCurrentCol = false;
    let nextColumn: any = null;

    editableCols.forEach((col: any) => {
      if (afterCurrentCol && !nextColumn) {
        nextColumn = col;
      }

      if (col.value === currentColName) {
        afterCurrentCol = true;
      }
    });

    return nextColumn;
  }

  private changeBackgroundColor(row: any, column: any): void {
    const currentColDialog = this.getDialogElement(row, column);
    const currentTableRow = currentColDialog.closest('tr');

    const allTableRows = Array.from(
      currentTableRow.parentElement.children,
    ) as any[];

    allTableRows.forEach((tRow: HTMLElement) => {
      tRow.classList.remove('selected-row');
    });

    currentTableRow.classList.add('selected-row');
  }

  private getRowUnder(currentRow: any, above: boolean = false): any {
    const sortedRows = (this.$refs.grid as any).internalCurrentItems;

    const rowsCopy = [...sortedRows];

    if (above) {
      rowsCopy.reverse();
    }

    let afterCurrentRow = false;
    let nextRow: any = null;

    rowsCopy.forEach((row: any) => {
      if (afterCurrentRow && !nextRow) {
        nextRow = row;
      }

      if (currentRow.$_row_key === row.$_row_key) {
        afterCurrentRow = true;
      }
    });

    return nextRow;
  }

  private showCancelDialog(): void {
    this.cancelDialogIsVisible = true;
  }

  private editDialogOnOpen(row: any, column: any): void {
    this.focusField(row, column);
    this.changeBackgroundColor(row, column);
    this.triggerFieldOnOpenEvent(row, column);
  }

  private createBackup(): void {
    this.dataBackup = Util.deepCopy(this.gridRows);

    this.dataBackup.forEach((row: any, i: number) => {
      row.$_row_key = i;
    });
  }

  private triggerFieldOnOpenEvent(row: any, column: any): void {
    const fieldId = this.getFieldId(row, column);
    let count = this.fieldTriggers.onOpen[fieldId] || 0;

    count++;

    this.fieldTriggers.onOpen[fieldId] = count;
  }

  private async saveButtonOnClick() {
    this.emitSave(this.gridRows)

    if (this.dataSource) {
      if (!this.preventSave) {
        await this.save();

        if (this.reloadAfterSave) {
          this.reload();
        }
      }
    }
  }

  private async reload() {
    const urlParams = {
      requestType: 'FilterData',
      ...this.filters,
    };

    try {
      const response = await Http.get(this.dataSource, urlParams);
  
      this.gridRows = response.data.dataset.data;
      this.setRowKeys(this.gridRows);
      this.createBackup();
  
      this.applyRowClass();
      this.emitAfterReload(this.gridRows);
    } catch (e) {
      this.setRowKeys([]);
      this.createBackup();
      this.emitAfterReload([]);

      const message = e.response ? e.response.data.error : InlineGrid.defaultErrorMessage;
      SnackbarUtils.showMessage(message, SnackbarUtils.TYPE_ERROR);
    }
  }

  private getCellStyle(row: any, column: any): string {
    let style = '';

    style = column.style ? `${column.style(row)};` : '';

    style += `font-weight: ${row.hasChanged ? 'bold' : 'normal'};`;

    return style;
  }

  private getCellClass(row: any, column: any): string {
    let cssClass = '';

    cssClass = column.cssClass ? column.cssClass(row) : '';

    return cssClass;
  }

  private getDialogElement(row: any, column: any): HTMLElement {
    const dialogElement = document.getElementById(
      `__grid_dialog_${row.$_row_key}-${column.value}`,
    ) as any;

    return dialogElement;
  }

  private getEditDialogId(row: any, column: any): string {
    const dialogId = '__grid_dialog_' + row.$_row_key + '-' + column.value;

    return dialogId;
  }

  private getFieldId(row: any, column: any): string {
    const fieldId = '__grid_field_' + row.$_row_key + '-' + column.value;

    return fieldId;
  }

  private getRowBackup(row: any): any {
    const rowBackup = this.dataBackup.find((backup: any) =>
      backup.$_row_key === row.$_row_key);

    return rowBackup;
  }

  private getSlotName(col: any): string {
    return `item.${col.value}`;
  }

  private setRowKeys(rows: object[]): void {
    rows.forEach((row: any, i: number) => {
      row.$_row_key = i;
    });
  }

  // Method responsible for return the changed rows
  private getChangedRows() {
    const changedRows = this.gridRows.filter((row: any) => row.hasChanged);

    return changedRows;
  }

  private async toolbarActionOnClick(action: any) {
    if (action.returnEditedRows) {
      action.onClick(this.getChangedRows());
    }
  }

  private detailsActionClicked(onClick: any) {
    onClick(this.selected);
  }

  private async applyRowClass() {
    if (!this.rowClass) {
      return;
    }

    await Util.setTimeout(10);

    const currentRows: any[] = (this.$refs.grid as any).internalCurrentItems;

    const dataTable: Element = (this.$refs.grid as any).$el;
    const tBody = Util.findChildRecursive(dataTable, 'tbody');
    const htmlRows = Array.from(tBody.children);

    for (let i = 0; i < currentRows.length; i++) {
      const row = currentRows[i];
      const htmlRow = htmlRows[i] as HTMLElement;

      this.removeCustomClasses(htmlRow);

      const rowClass = this.getRowClass(row);

      if (rowClass) {
        this.addCustomClass(htmlRow, rowClass);
      }
    }
  }

  private getRowClass(row: any): string {
    if (!this.rowClass) {
      return null;
    }

    if (typeof this.rowClass === 'string') {
      return this.rowClass;
    }

    const rowClass = this.rowClass(row);

    return rowClass;
  }

  private toggleSelectAll() {
    this.applyRowClass();
  }

  private onItemSelected(item: any) {
    this.applyRowClass();

    this.itemSelected(item);
  }

  private addCustomClass(
    htmlRow: HTMLElement,
    className: string,
  ) {
    htmlRow.classList.add(className);
    htmlRow.classList.add(InlineGrid.CUSTOM_CLASS_PREFIX + className);
  }

  private removeCustomClasses(
    htmlRow: HTMLElement
  ) {
    const classes = Array.from(htmlRow.classList);

    const customClasses = classes.filter((rowClass: string) => {
      const isCustom = rowClass.includes(InlineGrid.CUSTOM_CLASS_PREFIX);
      return isCustom;
    });

    customClasses.forEach((customClass: string) => {
      const classOriginalName = customClass.replace(InlineGrid.CUSTOM_CLASS_PREFIX, '');

      htmlRow.classList.remove(customClass);
      htmlRow.classList.remove(classOriginalName);
    });
  }
  // #endregion
}
